import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react'
import type { Paragraph } from '@/api/types/stowed-element'
import { paragraphsToPlainText } from '@/lib/shapes/shape-text'

/**
 * Keeps the live editing buffer in step with a model a chain reflow rewrote underneath it: the
 * trim-resync effect, the layout effect that re-asserts its caret, and the provenance reset.
 * Their order is load-bearing and matches the original component.
 */
export function useChainTrimResync({
  isEditing,
  hasNextInChain,
  hasPrevInChain,
  paragraphs,
  textValue,
  setTextValue,
  textareaRef,
  caretInitializedRef,
  initialCaretOffset,
  chainOverflowRelocatedRef,
  lastEmittedModelTextRef,
  emitSeqRef,
  setCaretIndex,
  setSelectionEndIndex,
}: {
  isEditing: boolean
  hasNextInChain: boolean
  hasPrevInChain: boolean
  paragraphs: Paragraph[]
  textValue: string
  setTextValue: (value: string) => void
  textareaRef: MutableRefObject<HTMLTextAreaElement | null>
  caretInitializedRef: MutableRefObject<boolean>
  initialCaretOffset: number | null | undefined
  chainOverflowRelocatedRef: MutableRefObject<boolean>
  lastEmittedModelTextRef: MutableRefObject<string | null>
  emitSeqRef: MutableRefObject<number>
  setCaretIndex: Dispatch<SetStateAction<number>>
  setSelectionEndIndex: Dispatch<SetStateAction<number>>
}) {
  /**
   * The caret the trim-resync below chose, **paired with the exact `textValue` it was measured
   * against**, waiting to be re-asserted after React commits that value. See the re-assert effect
   * underneath the resync for why the resync's own `setSelectionRange` is not enough.
   *
   * The value is what makes the pairing safe rather than merely hopeful. A resync fires from the
   * commit that still holds the PRE-trim `textValue`, and the effect below runs in that commit's
   * effect pass too — so a bare caret number gets consumed one commit early, by the pass whose
   * value the caret does not belong to, and the commit that actually writes the trimmed value then
   * finds nothing pending and leaves the caret where its own write collapsed it.
   *
   * `seq` retires an entry a later keystroke has overtaken. The pairing above assumes the resync's
   * value is the next one to be committed, and under key auto-repeat it is not: a repeat landing
   * before that commit calls `setTextValue` again, the resync's value never becomes `textValue`,
   * and the entry would otherwise sit here indefinitely — ready to yank the caret backwards the
   * next time `textValue` happened to match the string it was measured against.
   */
  const pendingResyncCaretRef = useRef<{ value: string; caret: number; seq: number } | null>(null)

  /**
   * Trim-resync: a chain reflow trimmed this frame's model out from under the live editor.
   *
   * `reflowAndPersistChain` now writes the edited frame's kept portion back like any other chain
   * member (WMPCM-9199). It used to exclude that frame, which left it holding text that had
   * already flowed downstream, so every later commit evicted the same text again and the next
   * frame gained a duplicate per debounce window. The live buffer has to follow the trim, or the
   * next keystroke re-emits the untrimmed text and rebuilds the duplicate from this side.
   *
   * Both directions matter. A trim is the obvious one, but the chain redistributes head-first and
   * fills every frame to capacity, so the same pass can pull the next frame's head up into this one
   * and make its model LONGER. Either way the buffer must follow: `handleTextChange` diffs the
   * textarea against `textValue` and applies those offsets to `paragraphs`, so the moment the two
   * describe different strings the next keystroke edits at the wrong offsets.
   *
   * Three guards keep it from firing on anything but an external write:
   *
   * - Chain members only — `hasNextInChain || hasPrevInChain`, not `hasNextInChain` alone. A
   *   chain-LAST frame's slice used to be unable to change while it was being edited, because the
   *   reflow was anchor-scoped to the edited frame and so never walked upstream. Head-scoping ends
   *   that: editing the last frame while an upstream frame has room does shrink its slice, and a
   *   `hasNextInChain`-only gate would leave the live buffer behind — the exact desync this effect
   *   exists to prevent, showing up as the user's next keystrokes landing at wrong offsets. A solo
   *   frame is still excluded: its model is never rewritten underneath the editor, it hard-blocks
   *   over-capacity input instead (`use-shape-text-editing.ts`).
   * - The model must differ from what this editor itself last emitted. That is the provenance
   *   test — every editor-originated write goes through `emitParagraphs`, so anything else is by
   *   definition someone else's. Comparing against `textValue` would instead resync on any
   *   normalisation `applyEditToParagraphs` performs, and would fight the user keystroke by
   *   keystroke.
   * - The caret must be collapsed. A range selection has its own anchor/focus to preserve, and
   *   rewriting the buffer under it would collapse it silently.
   *
   * A caret PAST the new end is the subtle case, and it used to abandon the resync outright
   * (`if (caret > modelText.length) return`) on the reasoning that such a caret sits in the text that
   * just flowed onward and `onChainOverflowCaret` (`use-chain-overflow-handoff.ts`) is already moving it into the next frame, so
   * clamping here would drag it back and fight the hand-off. That is only true when the hand-off
   * actually relocated the session — and it silently refuses whenever it cannot resolve a destination
   * (WMPCM-9212). Standing down there left `textValue` and `paragraphs` describing different strings,
   * which is exactly the state the paragraph above says must never exist: `handleTextChange` then
   * applied its diff offsets to the wrong model, so every further keystroke landed at this frame's
   * end and was re-evicted to the HEAD of the next frame — the user's text arriving in reverse.
   *
   * So the caret is clamped instead, unless a hand-off really did take ownership of it. Worst case
   * the text then keeps appending here and flows forward in document order, which is a legible
   * outcome; a desynced buffer never is.
   */
  useEffect(() => {
    if (!isEditing || !(hasNextInChain || hasPrevInChain)) return
    const ta = textareaRef.current

    if (!ta || ta.selectionStart !== ta.selectionEnd) return

    const modelText = paragraphsToPlainText(paragraphs)

    if (modelText === (lastEmittedModelTextRef.current ?? textValue)) return

    // `ta.selectionStart` only means something once this frame's edit session has placed its own
    // caret. On a chain hand-off it has not: the `focus()` and the `setSelectionRange` both live in
    // the caret-init effect (`use-shape-text-editing.ts`), which is declared AFTER this one and so
    // runs later in the same commit. Reading the still-unfocused node here yields 0, and the
    // re-assert layout effect below then applies that 0 on top of the offset the hand-off computed.
    //
    // That is what put the caret at the HEAD of the previous frame on Backspace at the start of a
    // linked frame — and, the caret then being at offset 0 of a frame that also has a predecessor,
    // made the next press repeat the hop one frame further up the chain. ArrowLeft never hit it
    // because it mutates no paragraphs, so the provenance check above short-circuits first.
    //
    // So while the session is uninitialised the caller's offset outranks the node. It still goes
    // through the clamp below, and still reaches the re-assert through `pendingResyncCaretRef` —
    // this only corrects WHICH caret is being carried, never the machinery that carries it.
    let caret =
      !caretInitializedRef.current && initialCaretOffset != null
        ? initialCaretOffset
        : (ta.selectionStart ?? 0)

    if (caret > modelText.length) {
      // A hand-off owns this caret and is placing it in the destination frame — leave it alone.
      if (chainOverflowRelocatedRef.current) return

      caret = modelText.length
    }

    lastEmittedModelTextRef.current = modelText
    setTextValue(modelText)
    // Deliberately NOT `ta.value = modelText` (WMPCM-9070, held-key repeat). Writing the model
    // straight onto a CONTROLLED node puts it out of step with `props.value`, which is still the
    // pre-trim string until the `setTextValue` above commits — and that commit is a whole task
    // away, because an update scheduled from a passive effect is raised at DEFAULT priority, not
    // the keystroke's discrete one. React's own `restoreStateIfNeeded`, which runs at the END OF
    // THIS KEYSTROKE'S EVENT, then finds `node.value !== props.value` and writes the pre-trim
    // string back — and every `value` assignment collapses the selection to the end of the text.
    //
    // For a single press nobody sees it: the re-assert below lands before the next keystroke and
    // puts the caret back. Hold Space or Backspace and the ~30 ms auto-repeat arrives INSIDE that
    // window, and the browser applies it at the collapsed caret — which is why a held key walked
    // the caret to the end of the frame and then (the caret now being past `consumedLength`) rode
    // the overflow hand-off into the next frame, while a tapped key was fine.
    //
    // Leaving the node alone removes the mismatch and with it the clobbering restore. The node
    // keeps the pre-trim string for the rest of the window, which is exactly what `textValue`
    // still describes, so a repeat landing there is diffed against the right string and applied at
    // the right offset — `handleTextChange` reads `textValue`, never the model.
    ta.setSelectionRange(caret, caret)
    setCaretIndex(caret)
    setSelectionEndIndex(caret)
    // Handed to the re-assert effect below, which is the assignment that actually sticks when this
    // resync runs inside an input event. Only when `textValue` really changes — otherwise no
    // re-render follows, the effect never fires, and the entry sits there until some later commit
    // happens to land on the same string.
    if (modelText !== textValue) {
      pendingResyncCaretRef.current = { value: modelText, caret, seq: emitSeqRef.current }
    }
  }, [
    caretInitializedRef,
    hasNextInChain,
    hasPrevInChain,
    initialCaretOffset,
    isEditing,
    paragraphs,
    setCaretIndex,
    setSelectionEndIndex,
    setTextValue,
    textValue,
    textareaRef,
    chainOverflowRelocatedRef,
    emitSeqRef,
    lastEmittedModelTextRef,
  ])

  /**
   * Re-asserts the caret the trim-resync chose, AFTER React has committed the value that goes with
   * it. Without this the caret jumps to the frame's last character on every keystroke that reflows
   * the chain — insert and Backspace alike.
   *
   * The textarea is CONTROLLED (`value={textValue}`). When the resync above runs inside an input
   * event's own effect pass, React's `restoreControlledState` still has the pre-trim `props.value`
   * and writes it back to the node, then the new value lands from the re-render — and each `value`
   * assignment collapses the selection to the end. Both happen after the effect body, so the
   * `setSelectionRange` there is simply overwritten.
   *
   * Invisible until the chain started flowing per keystroke: the resync only ever ran from the
   * 500 ms commit before, with no event in flight, and React's restore is a no-op when the node
   * already holds `props.value`.
   *
   * Keyed on `textValue` so it runs in the commit that applies the resync's own value — after
   * React's writes, not racing them. The immediate `setSelectionRange` in the resync stays for the
   * timer-driven case, where no re-render need follow.
   *
   * **A LAYOUT effect, and that is load-bearing (WMPCM-9070, held-key repeat).** React writes the
   * new `value` onto the node in this commit's mutation phase, which collapses the selection to the
   * end of the text; this puts it back. As a passive effect the repair was a whole task later, and
   * a held key's ~30 ms auto-repeat lands inside that gap and is applied at the collapsed caret —
   * the same window the resync's removed `ta.value` write opened at the other end. A layout effect
   * runs synchronously in the mutation phase's own commit, before paint and before the browser can
   * dispatch the next key event, so the collapsed caret is never observable.
   *
   * **Generalises past this frame:** any imperative `value` / `setSelectionRange` on a controlled
   * input performed from inside an input event's effect pass will be undone by React. Assert the
   * selection in the commit that applies the value, from a layout effect.
   */
  useLayoutEffect(() => {
    const pending = pendingResyncCaretRef.current

    // Wait, do not consume: this commit's value is not the one the caret was measured against, so
    // React has not finished writing yet and asserting here would be undone.
    if (!pending || pending.value !== textValue) return

    const ta = textareaRef.current

    // Overtaken by a later keystroke: the caret was measured against a model this editor has since
    // written past, so it describes a position that no longer means anything. Drop the caret
    // rather than apply it — the resync that follows that keystroke records the current one.
    //
    // **The value is still repaired, and that part is not optional.** This used to `return` here,
    // skipping the `ta.value` repair below along with the caret, which left the node holding what
    // React's restore had written while `props.value` said something else. `handleTextChange` then
    // diffed the next keystroke against a string the node did not hold — and since every `value`
    // assignment collapses the selection to the end of the text, that keystroke was applied at the
    // frame's END. It is one of the two ways a mid-frame Backspace in a linked chain came to delete
    // the frame's last character instead of the one before the caret.
    //
    // The node's current offset is carried across the repair rather than left to collapse: the
    // caret this entry described is stale, but wherever the node is pointing now is not.
    if (pending.seq !== emitSeqRef.current) {
      pendingResyncCaretRef.current = null

      if (ta && ta.value !== textValue) {
        const keep = Math.max(0, Math.min(ta.selectionStart ?? 0, textValue.length))

        ta.value = textValue
        ta.setSelectionRange(keep, keep)
      }

      return
    }

    pendingResyncCaretRef.current = null

    if (!ta) return

    const clamped = Math.max(0, Math.min(pending.caret, textValue.length))

    // Repair the value too, and BEFORE the selection — never bail on a mismatch. React's restore
    // can leave the node holding the pre-trim string, and a `ta.value !== textValue` guard here
    // (the obvious defensive one) is exactly what made an earlier version of this fix silently do
    // nothing: it bailed on that mismatch and left the caret wherever the last `value` write
    // collapsed it, which is the frame's end.
    //
    // Belt-and-braces in the measured ordering — the commit whose value matches `pending.value` has
    // already written it to the node, so this branch does not fire. Kept because the branch that
    // does fire is the one that silently loses the caret. Writing through the node's own (React
    // tracked) setter is deliberate: it is the same setter React's own commit writes through, so the
    // change tracker stays in step with `node.value` and the next real keystroke is still seen as a
    // change.
    if (ta.value !== textValue) ta.value = textValue

    ta.setSelectionRange(clamped, clamped)
    setCaretIndex(clamped)
    setSelectionEndIndex(clamped)
  }, [setCaretIndex, setSelectionEndIndex, textValue, textareaRef, emitSeqRef])

  // The provenance ref belongs to one edit session; a stale value would make the first external
  // write of the next session look like the editor's own.
  useEffect(() => {
    if (!isEditing) lastEmittedModelTextRef.current = null
  }, [isEditing, lastEmittedModelTextRef])
}
